import { useEffect, useState } from 'react';
import type { AuthMethodsInfo } from 'shared';
import { useFormErrors } from '../useFormErrors';
import FieldError from '../FieldError';
import PendingApproval from '../PendingApproval';
import { api, ApiError } from '../api';

interface Props {
  onSuccess: () => void;
}

type Mode = 'login' | 'register';

export default function Login({ onSuccess }: Props) {
  const [methods, setMethods] = useState<AuthMethodsInfo | null>(null);
  const [mode, setMode] = useState<Mode>('login');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const form = useFormErrors();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    api
      .authMethods()
      .then(setMethods)
      .catch(() =>
        // Сервер недоступен — показываем форму по паролю, она основная.
        setMethods({
          password: true,
          telegram: true,
          email: false,
          phone: false,
          registrationOpen: true,
        }),
      );
  }, []);

  async function submit(e: React.FormEvent) {
    e.preventDefault();

    /*
     * Проверяем сами, а не полагаемся на `required`.
     *
     * Браузерная подсказка выглядит по-разному в Safari, Chrome и webview
     * Telegram, а в последнем часто не показывается вовсе — человек жмёт
     * «Войти» и не понимает, почему ничего не происходит.
     */
    const bad: Record<string, string> = {};
    if (!login.trim()) bad.login = 'Введите логин';
    if (!password) bad.password = 'Введите пароль';
    else if (mode === 'register' && password.length < 8) {
      bad.password = 'Пароль не короче 8 символов';
    }
    if (mode === 'register' && !firstName.trim()) bad.firstName = 'Как вас зовут?';

    if (Object.keys(bad).length > 0) {
      setError(null);
      return form.fail(bad);
    }

    form.reset();
    setBusy(true);
    setError(null);
    try {
      if (mode === 'login') {
        await api.loginPassword(login.trim(), password);
      } else {
        await api.register({
          login: login.trim(),
          password,
          firstName: firstName.trim(),
          lastName: lastName.trim() || undefined,
          phone: phone.trim() || undefined,
        });
      }
      onSuccess();
    } catch (err) {
      if ((err as ApiError).reason === 'PENDING_APPROVAL') {
        setPending(true);
        setBusy(false);
        return;
      }
      setError((err as ApiError).message);
      setBusy(false);
    }
  }

  if (pending) return <PendingApproval onBack={() => setPending(false)} />;

  if (!methods) return <div className="empty">Загрузка…</div>;

  if (!methods.password) {
    return (
      <div className="auth-shell">
        <div className="card">
          <h2>Вход по паролю отключён</h2>
          <p className="meta">Откройте приложение через Telegram-бота.</p>
        </div>
      </div>
    );
  }

  const canRegister = mode === 'login' || methods.registrationOpen;

  return (
    <div className="auth-shell">
      <div className="card">
        <h2>{mode === 'login' ? 'Вход' : 'Регистрация'}</h2>
        <p className="meta" style={{ marginBottom: 16 }}>
          Биржа грузоперевозок. Заявки, отклики и рейсы в одном месте.
        </p>

        {error && <div className="error">{error}</div>}

        <form onSubmit={submit}>
          <div className="field">
            <label>Логин</label>
            <input
              {...form.field('login')}
              autoComplete="username"
              autoCapitalize="none"
              placeholder="ivanov"
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
            <FieldError form={form} name="login" />
          </div>

          <div className="field">
            <label>Пароль</label>
            <input
              {...form.field('password')}
              type="password"
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <FieldError form={form} name="password" />
            {mode === 'register' && (
              <div className="meta" style={{ marginTop: 4 }}>
                Не короче 8 символов.
              </div>
            )}
          </div>

          {mode === 'register' && (
            <>
              <div className="field-row">
                <div className="field">
                  <label>Имя</label>
                  <input
                    {...form.field('firstName')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <FieldError form={form} name="firstName" />
                </div>
                <div className="field">
                  <label>Фамилия</label>
                  <input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
              </div>

              <div className="field">
                <label>Телефон</label>
                <input
                  inputMode="tel"
                  placeholder="+79001234567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>

            </>
          )}

          <button type="submit" disabled={busy}>
            {busy ? 'Секунду…' : mode === 'login' ? 'Войти' : 'Зарегистрироваться'}
          </button>
        </form>

        {canRegister && (
          <button
            className="secondary"
            style={{ marginTop: 12 }}
            onClick={() => {
              setError(null);
              setMode(mode === 'login' ? 'register' : 'login');
            }}
          >
            {mode === 'login' ? 'Создать аккаунт' : 'У меня уже есть аккаунт'}
          </button>
        )}

        {methods.telegram && (
          <div className="meta" style={{ marginTop: 16 }}>
            Есть аккаунт в Telegram-боте? Откройте приложение прямо из него —
            вход произойдёт сам. Логин и пароль можно добавить позже в профиле.
          </div>
        )}

        {(methods.email || methods.phone) && (
          <div className="meta" style={{ marginTop: 8 }}>
            Вход по {methods.email ? 'почте' : ''}
            {methods.email && methods.phone ? ' и ' : ''}
            {methods.phone ? 'номеру телефона' : ''} также доступен.
          </div>
        )}
      </div>
    </div>
  );
}
